import React from 'react';
import { cn } from '../../shared/lib/cn';
import {
  ArrowTopRightOnSquareIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { Badge, Button, Spinner, Switch } from '../ui';
import {
  getConnectorCatalogEntry,
  getConnectorScopeLabel,
  getConnectorSummary,
} from '../data-connections/connectorCatalog';
import {
  useListConnectors,
  type ConnectorConfiguration,
} from '../../generated/connectors/connectors';
import { useGetAvailableConnectorTypesApiConnectorsTypesGet } from '../../generated/data-connectors/data-connectors';
import {
  isAgentMeshSourceType,
  type AgentMeshSourceType,
} from '../../lib/agent-mesh-sources';

export { AGENT_MESH_SOURCE_TYPES, type AgentMeshSourceType } from '../../lib/agent-mesh-sources';

function normalizeWorkspaceConfig(
  workspaceConfig: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  return workspaceConfig && typeof workspaceConfig === 'object' ? { ...workspaceConfig } : {};
}

function normalizeChatConfig(
  workspaceConfig: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const normalizedConfig = normalizeWorkspaceConfig(workspaceConfig);
  const chat = normalizedConfig.chat;
  return chat && typeof chat === 'object' ? { ...(chat as Record<string, unknown>) } : {};
}

export function getExplicitAgentMeshSources(
  workspaceConfig: Record<string, unknown> | null | undefined,
): AgentMeshSourceType[] | null {
  const normalizedConfig = normalizeWorkspaceConfig(workspaceConfig);
  const chatConfig = normalizeChatConfig(workspaceConfig);
  const rawSources = Array.isArray(chatConfig.retrieval_sources)
    ? chatConfig.retrieval_sources
    : normalizedConfig.retrieval_sources;
  if (!Array.isArray(rawSources)) {
    return null;
  }

  return Array.from(
    new Set(
      rawSources
        .map((source) => String(source || '').trim().toLowerCase())
        .filter((source): source is AgentMeshSourceType => isAgentMeshSourceType(source)),
    ),
  );
}

export function getEffectiveAgentMeshSources(
  workspaceConfig: Record<string, unknown> | null | undefined,
  _connectedSourceTypes: AgentMeshSourceType[],
): AgentMeshSourceType[] {
  const explicitSources = getExplicitAgentMeshSources(workspaceConfig);
  return explicitSources ?? [];
}

export type PlannerMode = 'auto' | 'on' | 'off';

function normalizePlannerMode(value: unknown): PlannerMode {
  const normalized = String(value || 'auto').trim().toLowerCase();
  if (['on', 'enabled', 'true', '1', 'always'].includes(normalized)) return 'on';
  if (['off', 'disabled', 'false', '0', 'never'].includes(normalized)) return 'off';
  return 'auto';
}

export function getAgentMeshPlannerMode(
  workspaceConfig: Record<string, unknown> | null | undefined,
): PlannerMode {
  const normalizedConfig = normalizeWorkspaceConfig(workspaceConfig);
  const chatConfig = normalizeChatConfig(workspaceConfig);
  return normalizePlannerMode(chatConfig.planner_mode ?? normalizedConfig.chat_planner_mode);
}

export function plannerModeLabel(mode: PlannerMode): string {
  if (mode === 'on') return 'Planner forced on';
  if (mode === 'off') return 'Planner disabled';
  return 'Planner auto';
}

function latestConnectorByConnectorType(
  connectors: ConnectorConfiguration[],
): Record<string, ConnectorConfiguration> {
  const entries = [...connectors].sort((a, b) => {
    const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
    const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
    return bTime - aTime;
  });

  return entries.reduce<Record<string, ConnectorConfiguration>>((acc, connector) => {
    const connectorType = String(connector.connector_type);
    if (!acc[connectorType]) {
      acc[connectorType] = connector;
    }
    return acc;
  }, {});
}

function getExplicitWorkspaceConnectorTypes(
  workspaceConfig: Record<string, unknown> | null | undefined,
): string[] | null {
  const normalizedConfig = normalizeWorkspaceConfig(workspaceConfig);
  const chatConfig = normalizeChatConfig(workspaceConfig);
  const rawSources = Array.isArray(chatConfig.retrieval_sources)
    ? chatConfig.retrieval_sources
    : normalizedConfig.retrieval_sources;
  if (!Array.isArray(rawSources)) {
    return null;
  }

  return Array.from(
    new Set(
      rawSources
        .map((source) => String(source || '').trim().toLowerCase())
        .filter(Boolean),
    ),
  );
}

function getEffectiveWorkspaceConnectorTypes(
  workspaceConfig: Record<string, unknown> | null | undefined,
  _connectedSourceTypes: string[],
): string[] {
  const explicitSources = getExplicitWorkspaceConnectorTypes(workspaceConfig);
  return explicitSources ?? [];
}

const CONNECTOR_CATEGORY_ORDER: Record<string, number> = {
  crm: 0,
  people_data: 1,
  meeting_intelligence: 2,
  collaboration: 3,
  cloud_storage: 4,
  catalog: 5,
  hr_data: 6,
};

function connectorCategoryLabel(category: string | undefined): string {
  switch (category) {
    case 'crm':
      return 'CRM & customer data';
    case 'people_data':
      return 'People & company data';
    case 'meeting_intelligence':
      return 'Meetings & notes';
    case 'collaboration':
      return 'Collaboration & documents';
    case 'cloud_storage':
      return 'Warehouses & storage';
    case 'catalog':
      return 'Data catalogs';
    case 'hr_data':
      return 'People systems';
    default:
      return 'Other sources';
  }
}

type WorkspaceConnectorRow = {
  type: string;
  name: string;
  description: string;
  category: string;
  available: boolean;
};

interface WorkspaceConnectorAccessPanelProps {
  workspaceConfig: Record<string, unknown> | null | undefined;
  onSaveWorkspaceConfig: (nextWorkspaceConfig: Record<string, unknown>) => Promise<void>;
  onOpenConnectionManager: (
    sourceType?: string,
    options?: { create?: boolean },
  ) => void;
  canManageConnections: boolean;
}

export function WorkspaceConnectorAccessPanel({
  workspaceConfig,
  onSaveWorkspaceConfig,
  onOpenConnectionManager,
  canManageConnections,
}: WorkspaceConnectorAccessPanelProps) {
  const { data: connectorsResponse, isLoading: isLoadingConnectors } = useListConnectors(
    {}, // Fetch all connectors (including disabled)
    { query: { enabled: canManageConnections, staleTime: 15000 } },
  );
  const { data: connectorTypesResponse, isLoading: isLoadingConnectorTypes } =
    useGetAvailableConnectorTypesApiConnectorsTypesGet({
      query: { enabled: canManageConnections, staleTime: 60000 },
    });
  const [savingSourceType, setSavingSourceType] = React.useState<string | null>(null);

  const allConnectors = React.useMemo(
    () => connectorsResponse?.connectors || [],
    [connectorsResponse],
  );

  const latestByType = React.useMemo(
    () => latestConnectorByConnectorType(allConnectors),
    [allConnectors],
  );

  const connectedSourceTypes = React.useMemo(
    () => Object.keys(latestByType).sort(),
    [latestByType],
  );

  const effectiveSourceTypes = React.useMemo(
    () => getEffectiveWorkspaceConnectorTypes(workspaceConfig, connectedSourceTypes),
    [workspaceConfig, connectedSourceTypes],
  );

  const explicitSourceTypes = React.useMemo(
    () => getExplicitWorkspaceConnectorTypes(workspaceConfig),
    [workspaceConfig],
  );

  const connectorRows = React.useMemo<WorkspaceConnectorRow[]>(() => {
    const rowsByType = new Map<string, WorkspaceConnectorRow>();

    (connectorTypesResponse?.connector_types || []).forEach((connectorType) => {
      const type = String(connectorType.type);
      const catalog = getConnectorCatalogEntry(type);
      rowsByType.set(type, {
        type,
        name: connectorType.name || catalog.label,
        description: connectorType.description || catalog.description,
        category: connectorType.category || 'other',
        available: connectorType.available !== false,
      });
    });

    allConnectors.forEach((connector) => {
      const type = String(connector.connector_type);
      if (!rowsByType.has(type)) {
        const catalog = getConnectorCatalogEntry(type);
        rowsByType.set(type, {
          type,
          name: catalog.label,
          description: catalog.description,
          category: 'other',
          available: true,
        });
      }
    });

    return Array.from(rowsByType.values()).sort((a, b) => {
      const categoryOrder =
        (CONNECTOR_CATEGORY_ORDER[a.category] ?? 99) - (CONNECTOR_CATEGORY_ORDER[b.category] ?? 99);
      if (categoryOrder !== 0) return categoryOrder;
      return a.name.localeCompare(b.name);
    });
  }, [allConnectors, connectorTypesResponse?.connector_types]);

  const connectorsByType = React.useMemo(() => {
    return allConnectors.reduce<Record<string, ConnectorConfiguration[]>>((acc, connector) => {
      const type = String(connector.connector_type);
      acc[type] = [...(acc[type] || []), connector];
      return acc;
    }, {});
  }, [allConnectors]);

  const activeConnectorRows = React.useMemo(
    () => connectorRows.filter((row) => latestByType[row.type]),
    [connectorRows, latestByType],
  );

  const isLoading = isLoadingConnectors || isLoadingConnectorTypes;

  const saveWorkspaceSources = React.useCallback(
    async (nextSources: string[], sourceType: string) => {
      const nextWorkspaceConfig = normalizeWorkspaceConfig(workspaceConfig);
      const nextChatConfig = normalizeChatConfig(workspaceConfig);
      nextChatConfig.retrieval_sources = Array.from(new Set(nextSources)).sort();
      nextWorkspaceConfig.chat = nextChatConfig;

      setSavingSourceType(sourceType);
      try {
        await onSaveWorkspaceConfig(nextWorkspaceConfig);
      } finally {
        setSavingSourceType(null);
      }
    },
    [onSaveWorkspaceConfig, workspaceConfig],
  );

  const handleToggleSource = React.useCallback(
    async (sourceType: string, enabled: boolean) => {
      const nextSources = new Set(effectiveSourceTypes);
      if (enabled) {
        nextSources.add(sourceType);
      } else {
        nextSources.delete(sourceType);
      }

      await saveWorkspaceSources(Array.from(nextSources), sourceType);
    },
    [effectiveSourceTypes, saveWorkspaceSources],
  );

  const enabledCount = effectiveSourceTypes.length;
  const connectedCount = activeConnectorRows.length;
  const selectionModeLabel = explicitSourceTypes ? 'Customized for this workspace' : 'No workspace sources selected';
  const plannerMode = getAgentMeshPlannerMode(workspaceConfig);

  return (
    <section className="overflow-hidden rounded-[20px] border border-gray-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.06)] dark:border-dark-border dark:bg-dark-surface">
      <div className="flex flex-col gap-3 border-b border-gray-200 px-5 py-5 dark:border-dark-border">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              <LinkIcon className="h-3.5 w-3.5" />
              Agent Mesh
            </div>
            <h3 className="mt-1 font-title text-[28px] leading-tight text-charcoal dark:text-white">
              Connector access
            </h3>
            <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">
              Every Data Connections source type is listed here. Configure tenant connections centrally, then enable or disable each source for this workspace.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant="secondary">{selectionModeLabel}</Badge>
            <Badge variant="secondary">{plannerModeLabel(plannerMode)}</Badge>
            <Badge variant="secondary">{enabledCount} enabled</Badge>
            <Badge variant="secondary">{connectedCount} configured</Badge>
          </div>
        </div>
      </div>

      {!canManageConnections ? (
        <div className="px-5 py-5 text-sm text-gray-500 dark:text-gray-400">
          Workspace source access is managed by admins with connection permissions.
        </div>
      ) : isLoading && connectorRows.length === 0 ? (
        <div className="flex items-center gap-2 px-5 py-8 text-sm text-gray-500 dark:text-gray-400">
          <Spinner size="sm" />
          Loading connector types...
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
                <th className="px-5 py-3">Connector</th>
                <th className="px-5 py-3">Tenant connection</th>
                <th className="px-5 py-3">Workspace access</th>
                <th className="px-5 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
              {connectorRows.map((row) => {
                const catalog = getConnectorCatalogEntry(row.type);
                const Icon = catalog.icon;
                const connector = latestByType[row.type] ?? null;
                const matchingConnectors = connectorsByType[row.type] || [];
                const isConfigured = Boolean(connector);
                const isEnabledForWorkspace = effectiveSourceTypes.includes(row.type);
                const isSaving = savingSourceType === row.type;
                const connectorSummary = connector
                  ? getConnectorSummary(connector.connector_type, connector.sync_config)
                  : null;

                return (
                  <tr
                    key={row.type}
                    className={cn(
                      'transition-colors hover:bg-gray-50 dark:hover:bg-dark-surface-2',
                      isLoading && 'opacity-60',
                    )}
                  >
                    <td className="px-5 py-4 align-middle">
                      <div className="flex min-w-0 items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-600 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold text-charcoal dark:text-white">{row.name}</p>
                            <Badge variant="secondary">{connectorCategoryLabel(row.category)}</Badge>
                            {!row.available && <Badge variant="outline">Soon</Badge>}
                          </div>
                          <p className="mt-1 max-w-xl text-xs leading-5 text-gray-500 dark:text-gray-400">
                            {row.description}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 align-middle">
                      {connector ? (
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium text-charcoal dark:text-white">{connector.connector_name}</p>
                            {connector.is_enabled ? (
                              connector.is_healthy ? (
                                <Badge variant="success">Configured</Badge>
                              ) : (
                                <Badge variant="warning">Needs review</Badge>
                              )
                            ) : (
                              <Badge variant="warning">Tenant disabled</Badge>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {getConnectorScopeLabel(connector.sync_config)}
                            {connectorSummary ? ` - ${connectorSummary}` : ''}
                            {matchingConnectors.length > 1 ? ` - ${matchingConnectors.length} connections` : ''}
                          </p>
                        </div>
                      ) : (
                        <div>
                          <Badge variant="secondary">Not configured</Badge>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Configure this source in Data Connections before enabling it here.
                          </p>
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4 align-middle">
                      {isConfigured ? (
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={isEnabledForWorkspace}
                            onCheckedChange={(checked) => void handleToggleSource(row.type, checked)}
                            disabled={isSaving || isLoading}
                            aria-label={`Use ${row.name} in this workspace`}
                          />
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            {isSaving
                              ? 'Saving...'
                              : isEnabledForWorkspace
                                ? 'On for this workspace'
                                : 'Off for this workspace'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">Unavailable until configured</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right align-middle">
                      {isConfigured ? (
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => onOpenConnectionManager(row.type)}
                        >
                          View
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onOpenConnectionManager(row.type, { create: true })}
                          disabled={!row.available}
                        >
                          {row.available ? 'Configure' : 'Coming soon'}
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
